import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setAccessToken, getAccessToken, formatApiError } from "../services/api";

/**
 * Real AuthContext (JWT + httpOnly cookies) for AFRICTAL V1.
 *
 * Roles are aligned with the Keycloak realm target so the whole app
 * can be re-pointed at Keycloak without touching consumers.
 */

export const ROLES = {
  TALENT: "talent",
  EMPLOYEUR: "employeur",
  FREELANCE: "freelance",
  UNIVERSITE: "universite",
  ENTREPRENEUR: "entrepreneur",
  INVESTISSEUR: "investisseur",
  COACH: "coach",
  ADMIN: "admin",
  SUPER_ADMIN: "super-admin",
};

export const ROLE_LABELS = {
  talent: "Talent",
  employeur: "Employeur",
  freelance: "Freelance",
  universite: "Université",
  entrepreneur: "Entrepreneur",
  investisseur: "Investisseur",
  coach: "Coach",
  admin: "Administrateur",
  "super-admin": "Super Administrateur",
};

const ADMIN_ROLES = new Set([ROLES.ADMIN, ROLES.SUPER_ADMIN]);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authenticated | anonymous

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setStatus("authenticated");
    } catch {
      setUser(null);
      setStatus("anonymous");
      setAccessToken(null);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) setAccessToken(data.access_token);
    setUser(data.user);
    setStatus("authenticated");
    return data.user;
  }, []);

  const register = useCallback(async ({ email, password, name, role }) => {
    const { data } = await api.post("/auth/register", { email, password, name, role });
    if (data.access_token) setAccessToken(data.access_token);
    setUser(data.user);
    setStatus("authenticated");
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setAccessToken(null);
    setUser(null);
    setStatus("anonymous");
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      return data.user;
    } catch {
      return null;
    }
  }, []);

  const value = {
    user,
    status,
    isAuthenticated: status === "authenticated",
    isAdmin: !!user && ADMIN_ROLES.has(user.role),
    login,
    register,
    logout,
    refreshUser,
    formatError: formatApiError,
    getAccessToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
